import React, { useMemo } from "react";
import { Alert, Form, Input, Modal, Radio, Select } from "antd";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import AircraftSelector from "../../../../components/common/AircraftSelector";
import { UTCDateRangePicker } from "../../../../components/DateTimeInput";
import useScheduleDraftCreateFormOptions from "../hooks/useScheduleDraftCreateFormOptions";
import { buildV1ScheduleOptions } from "../lib/v1ScheduleSourceOptions";
import { buildCreateScheduleDraftSubmission } from "../services/scheduleDraftPayloadBuilders";

dayjs.extend(utc);

const CREATE_MODE_OPTIONS = [
    { label: "空白航班计划", value: "BLANK" },
    { label: "从 V1 版导入", value: "FROM_V1" },
];

function buildV1SchedulePeriodRange(schedule) {
    const start = schedule?.start_date ? dayjs.utc(schedule.start_date) : null;
    const end = schedule?.end_date ? dayjs.utc(schedule.end_date) : null;
    if (!start?.isValid() || !end?.isValid()) {
        return undefined;
    }
    return [start, end];
}

const ScheduleDraftCreateDialog = ({ open, confirmLoading, onCancel, onSubmit }) => {
    const [form] = Form.useForm();
    const sourceType = Form.useWatch("sourceType", form) || "BLANK";
    const { aircrafts, isLoading: aircraftLoading, v1Schedules, v1SchedulesLoading } = useScheduleDraftCreateFormOptions(open);
    const v1ScheduleOptions = useMemo(() => buildV1ScheduleOptions(v1Schedules), [v1Schedules]);
    const v1SchedulesById = useMemo(() => new Map((v1Schedules || []).map((schedule) => [String(schedule.id), schedule])), [v1Schedules]);

    const handleCreateModeChange = (event) => {
        if (event.target.value === "BLANK") {
            form.setFieldsValue({ sourceScheduleId: undefined });
        }
    };

    const handleV1ScheduleChange = (scheduleId) => {
        const schedule = v1SchedulesById.get(String(scheduleId));
        if (!schedule) {
            return;
        }

        form.setFieldsValue({
            name: schedule.name ? `${schedule.name}（旧版导入）` : "旧版导入航班计划",
            periodRange: buildV1SchedulePeriodRange(schedule),
            aircraftScope: schedule.aircraft_reg ? [schedule.aircraft_reg] : [],
        });
    };

    const handleOk = async () => {
        try {
            const values = await form.validateFields();
            await onSubmit(buildCreateScheduleDraftSubmission(values));
            form.resetFields();
        } catch (error) {
            if (error?.errorFields) {
                return;
            }
            throw error;
        }
    };

    const handleCancel = () => {
        form.resetFields();
        onCancel();
    };

    return (
        <Modal
            title="新建航班计划"
            open={open}
            onOk={handleOk}
            onCancel={handleCancel}
            okText={sourceType === "FROM_V1" ? "创建并导入" : "创建并进入"}
            cancelText="取消"
            confirmLoading={confirmLoading}
            destroyOnClose
        >
            <Form form={form} layout="vertical" initialValues={{ sourceType: "BLANK" }}>
                <Form.Item name="sourceType" label="创建方式" rules={[{ required: true, message: "请选择创建方式" }]}>
                    <Radio.Group options={CREATE_MODE_OPTIONS} optionType="button" buttonStyle="solid" onChange={handleCreateModeChange} />
                </Form.Item>

                {sourceType === "FROM_V1" ? (
                    <>
                        <Alert
                            type="info"
                            showIcon
                            message="创建时会直接把所选 V1 航班导入主编辑轨；若来源为空或导入失败，航班计划不会静默创建为空轨。"
                            style={{ marginBottom: 12 }}
                        />
                        <Form.Item name="sourceScheduleId" label="旧版航班计划" rules={[{ required: true, message: "请选择旧版航班计划" }]}>
                            <Select
                                showSearch
                                loading={v1SchedulesLoading}
                                placeholder="按计划名、飞机、日期范围、状态、航班数或更新时间搜索"
                                optionFilterProp="label"
                                options={v1ScheduleOptions}
                                onChange={handleV1ScheduleChange}
                            />
                        </Form.Item>
                    </>
                ) : null}

                <Form.Item name="name" label="航班计划名称" rules={[{ required: true, message: "请输入航班计划名称" }]}>
                    <Input placeholder="例如：华北货运周编排计划" maxLength={100} />
                </Form.Item>

                <Form.Item name="description" label="说明">
                    <Input.TextArea rows={3} maxLength={500} placeholder="说明该航班计划的范围、来源或当前意图" />
                </Form.Item>

                <Form.Item name="periodRange" label="编排时间范围" rules={[{ required: true, message: "请选择时间范围" }]}>
                    <UTCDateRangePicker style={{ width: "100%" }} format="YYYY-MM-DD" placeholder={["开始日期", "结束日期"]} />
                </Form.Item>

                <Form.Item name="aircraftScope" label="飞机范围" rules={sourceType === "BLANK" ? [{ required: true, message: "请选择至少一架飞机" }] : []}>
                    <AircraftSelector
                        mode="multiple"
                        allowClear
                        valueType="registration"
                        displayMode="detailed"
                        aircrafts={aircrafts}
                        loading={aircraftLoading}
                        placeholder="选择参与编排的飞机"
                        style={{ width: "100%" }}
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default ScheduleDraftCreateDialog;
